import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

const COMMIT_DELAY_MS = 250;

const parse = (text: string): number | null => (text ? parseFloat(text) : null);
const format = (value: number | null): string =>
  value == null ? '' : String(value);

// Puts the committed bound back into a field left with an unfinished number. State may
// already be '' (the DOM's reading of the unfinished text), and then React sees nothing
// to write: the leftover text is cleared by hand.
const restoreCommitted = (
  input: HTMLInputElement | null,
  committed: number | null,
  setText: (text: string) => void,
  setUnfinished: (unfinished: boolean) => void
): string => {
  const text = format(committed);
  if (input) input.value = text;
  setText(text);
  setUnfinished(false);
  return text;
};

interface NumericRangeFilterProps {
  min: number | null;
  max: number | null;
  onChange: (min: number | null, max: number | null) => void;
  step: string;
  minPlaceholder: string;
  maxPlaceholder: string;
}

/**
 * Min/Max pair that shows typed text at once and commits it after a typing pause, so a
 * bound like `10000` is one filter change, not five.
 */
export const NumericRangeFilter: React.FC<NumericRangeFilterProps> = ({
  min,
  max,
  onChange,
  step,
  minPlaceholder,
  maxPlaceholder,
}) => {
  const id = useId();
  const [minText, setMinText] = useState(() => format(min));
  const [maxText, setMaxText] = useState(() => format(max));
  const minRef = useRef<HTMLInputElement>(null);
  const maxRef = useRef<HTMLInputElement>(null);
  // An unfinished number (`1.`, `-`, `5e`) reads as '' from a number input but is not a
  // cleared bound: it never commits. `onInput` tracks it because `onChange` stays silent
  // while `.value` stays '' (empty -> `-`, `-` -> empty).
  const [minUnfinished, setMinUnfinished] = useState(false);
  const [maxUnfinished, setMaxUnfinished] = useState(false);

  // The parent passes a fresh closure every render; the timer must neither restart on
  // each parent render nor commit through a closure over stale filters.
  const onChangeRef = useRef(onChange);
  useLayoutEffect(() => {
    onChangeRef.current = onChange;
  });

  // A store write from elsewhere that changes this range (Clear all, chip X, saved view,
  // "Filter by value") reaches the text during render, not in an effect, so the commit
  // effect below never sees new props next to old text and can't re-arm a commit of the
  // replaced value. Text that already parses to the stored number stays, so `1.50`
  // survives the commit of `1.5`.
  const [shown, setShown] = useState({ min, max });
  if (!Object.is(shown.min, min) || !Object.is(shown.max, max)) {
    setShown({ min, max });
    if (parse(minText) !== min) setMinText(format(min));
    if (parse(maxText) !== max) setMaxText(format(max));
  }

  // Each keystroke re-arms the timer and a store write of this range cancels it. Not
  // `useDebouncedValue`: its lagging copy would commit the old text again after Clear all.
  useEffect(() => {
    if (minUnfinished || maxUnfinished) return;
    const nextMin = parse(minText);
    const nextMax = parse(maxText);
    if (nextMin === min && nextMax === max) return;
    const timer = setTimeout(
      () => onChangeRef.current(nextMin, nextMax),
      COMMIT_DELAY_MS
    );
    return () => clearTimeout(timer);
  }, [minText, maxText, min, max, minUnfinished, maxUnfinished]);

  // Blur commits at once, clamped against what the other field shows. A window or tab
  // switch also blurs the field but keeps it focused: nothing happens then, so the user
  // comes back to the same text (a valid draft still commits on the pause).
  const commit = (nextMin: number | null, nextMax: number | null) => {
    if (nextMin !== min || nextMax !== max) onChange(nextMin, nextMax);
  };

  const handleMinBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.currentTarget === document.activeElement) return;
    const text = minUnfinished
      ? restoreCommitted(minRef.current, min, setMinText, setMinUnfinished)
      : minText;
    const nextMin = parse(text);
    const nextMax = parse(maxText);
    if (nextMin !== null && nextMax !== null && nextMin > nextMax) {
      setMinText(maxText);
      commit(nextMax, nextMax);
    } else {
      commit(nextMin, nextMax);
    }
  };

  const handleMaxBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.currentTarget === document.activeElement) return;
    const text = maxUnfinished
      ? restoreCommitted(maxRef.current, max, setMaxText, setMaxUnfinished)
      : maxText;
    const nextMin = parse(minText);
    const nextMax = parse(text);
    if (nextMin !== null && nextMax !== null && nextMax < nextMin) {
      setMaxText(minText);
      commit(nextMin, nextMin);
    } else {
      commit(nextMin, nextMax);
    }
  };

  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <label
          htmlFor={`${id}-min`}
          className="mb-1 block text-xs text-gray-500"
        >
          Min
        </label>
        <input
          ref={minRef}
          id={`${id}-min`}
          type="number"
          step={step}
          value={minText}
          onChange={(e) => setMinText(e.target.value)}
          onInput={(e) => setMinUnfinished(e.currentTarget.validity.badInput)}
          onBlur={handleMinBlur}
          className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:ring-blue-500"
          placeholder={minPlaceholder}
        />
      </div>
      <div>
        <label
          htmlFor={`${id}-max`}
          className="mb-1 block text-xs text-gray-500"
        >
          Max
        </label>
        <input
          ref={maxRef}
          id={`${id}-max`}
          type="number"
          step={step}
          value={maxText}
          onChange={(e) => setMaxText(e.target.value)}
          onInput={(e) => setMaxUnfinished(e.currentTarget.validity.badInput)}
          onBlur={handleMaxBlur}
          className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:ring-blue-500"
          placeholder={maxPlaceholder}
        />
      </div>
    </div>
  );
};
